import { HighlightIcon, Skeleton, SkeletonText } from '@dev-spendesk/grapes';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { QueryError } from 'src/core/common/components/QueryError';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { getDimensionIcon } from 'src/core/modules/budgets/budgets-by-dimensions/models/dimension';

import { BudgetRow } from './BudgetRow';
import { useExpenseCategoriesBreakdownQuery } from '../../../../hooks/useExpenseCategoriesBreakdownQuery';
import { isBudgetBreakdownEmpty } from '../../../../models/breakdown';
import {
  isArchivedExpenseCategory,
  sortExpenseCategoryBreakdownsByExpenseCategory,
} from '../../../../models/expenseCategoryBreakdown';
import { type PeriodRangePayload } from '../../../../models/periodRange';

export const BudgetExpenseCategoriesList = ({
  budgetId,
  periodRange,
  rowClassName,
}: {
  budgetId: string;
  periodRange: PeriodRangePayload;
  rowClassName?: string;
}) => {
  const history = useHistory();
  const companyId = useCompanyId();
  const { t } = useTranslation('global');
  const expenseCategoriesBreakdownQueryState =
    useExpenseCategoriesBreakdownQuery(budgetId, periodRange);

  const expenseCategoryBreakdownRows =
    expenseCategoriesBreakdownQueryState.status === 'success'
      ? expenseCategoriesBreakdownQueryState.data.expenseCategoriesBreakdown.filter(
          (row) =>
            !isBudgetBreakdownEmpty(row) &&
            row.expenseCategory.id !== 'default',
        )
      : [];

  if (!expenseCategoryBreakdownRows.length) {
    return null;
  }

  if (expenseCategoriesBreakdownQueryState.status === 'error') {
    return (
      <QueryError
        queryError={expenseCategoriesBreakdownQueryState.error}
        componentType="Callout"
      />
    );
  }

  if (expenseCategoriesBreakdownQueryState.status === 'loading') {
    return <SkeletonRow />;
  }

  const handleClick = (expenseCategoryId: string) => {
    const budgetsPageRoute = routeFor(routes.SUB_BUDGET_OVERVIEW.path, {
      id: budgetId,
      company: companyId,
      subBudgetId: expenseCategoryId,
    });
    history.push(budgetsPageRoute);
  };

  return sortExpenseCategoryBreakdownsByExpenseCategory(
    expenseCategoryBreakdownRows,
    t,
  ).map((breakdown) => {
    const isArchived = isArchivedExpenseCategory(
      breakdown.expenseCategory.id,
      expenseCategoriesBreakdownQueryState.data.expenseCategories,
    );
    return (
      <BudgetRow
        key={breakdown.expenseCategory.id}
        rowName={
          breakdown.expenseCategory.isOther
            ? t('budget.breakdownSpendings.unassignedExpenseCategory')
            : breakdown.expenseCategory.name
        }
        onRowClick={() => {
          if (isArchived) {
            return;
          }
          handleClick(breakdown.expenseCategory.id);
        }}
        rowIcon={
          <HighlightIcon
            size={32}
            {...getDimensionIcon({ type: 'costCenter' })}
          />
        }
        breakdown={breakdown}
        className={rowClassName}
        isArchived={isArchived}
      />
    );
  });
};

const SkeletonRow = () =>
  Array.from({ length: 4 }).map(() => (
    <div
      key={`skeleton-row-${Math.random()}`}
      className="mx-16 grid grid-cols-5 items-center gap-16 py-12"
    >
      <SkeletonText width="120px" size="xl" />
      <div className="flex items-center gap-8">
        <Skeleton width="160px" height="8px" />
        <SkeletonText width="60px" size="m" />
      </div>
      <SkeletonText width="100px" size="xl" />
      <SkeletonText width="80px" className="justify-self-end" size="xl" />
      <SkeletonText width="80px" className="justify-self-end" size="xl" />
    </div>
  ));
